import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useToastr } from '@laxmimanogna/code-quick-components';
import AppColors from '../../../constants/AppColors';
import ROUTES from '../../../constants/Routes';
import { FONT_FAMILY } from '../../../constants/Theme';
import { AuditSheetContext } from '../../../providers/AuditSheetProvider';
import { AuthContext } from '../../../providers/AuthProvider';
import { isSheetEditableBy } from '../sheetAccess';

const BATCH_ITEM_KEY = '__batch__';

/**
 * What `onNavigateAway` reports back before the navigator leaves a sheet.
 * Only OK lets the navigation through.
 */
export const NAVIGATE_AWAY = {
  OK: 'ok',
  // An autosave is still running, so the latest edit is not confirmed yet.
  SAVING: 'saving',
  // The save before leaving failed.
  FAILED: 'failed',
};

/**
 * Footer navigator that lets a reviewer move between the batch audit sheet and
 * every chart that belongs to that batch, without going back to the audit list.
 *
 * Each entry carries who the chart is assigned to, because only the assigned
 * auditor can edit a sheet; everything else opens read only.
 *
 * The list is whatever the dashboard endpoint returns for the batch, which the
 * API already scopes to the caller (ClientDashboardViewSet.list): a manager or
 * admin gets every chart of the batch, an auditor only the ones assigned to
 * them, a qa only theirs. So this lists exactly what the user is allowed to
 * open, and there is no separate visibility rule to apply here. The batch entry
 * itself is always openable -- get_batch_object_or_403_404 lets any internal
 * role read a batch sheet.
 */
export default function BatchChartNavigator({
  batchId,
  currentChartId,
  isBatchView,
  onNavigateAway,
  isSaving = false,
}) {
  const navigate = useNavigate();
  const toast = useToastr();
  const { getBatchCharts } = useContext(AuditSheetContext);
  const { currentUser } = useContext(AuthContext);

  const [batch, setBatch] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    let isActive = true;

    if (!batchId) {
      setBatch(null);
      return undefined;
    }

    getBatchCharts(batchId).then(response => {
      if (isActive) setBatch(response);
    });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const items = useMemo(() => {
    if (!batch) return [];

    const chartCount = batch.charts.length;

    const batchItem = {
      key: BATCH_ITEM_KEY,
      isBatch: true,
      title: `Batch ${batch.batchName || batch.batchId}`,
      caption: `${chartCount} ${chartCount === 1 ? 'chart' : 'charts'}`,
      route: ROUTES.VIEW_BATCH_AUDIT_SHEET.replace(':batchId', batch.batchId),
    };

    const chartItems = batch.charts.map((chart, index) => {
      const isEditable = isSheetEditableBy(chart, currentUser);

      return {
        key: String(chart.id),
        isBatch: false,
        title: chart.name || `Chart ${chart.id}`,
        caption: `${index + 1} of ${chartCount}`,
        auditor: chart.auditor?.full_name || 'Unassigned',
        status: chart.status,
        isEditable,
        // Open the route this chart actually resolves to. The audit sheet would
        // redirect an uneditable chart to the view route anyway, but going
        // straight there avoids a flash of the editable sheet and keeps the URL
        // honest about what the badge promised.
        route: isEditable
          ? ROUTES.AUDIT_SHEET.replace(':uploadId', chart.id)
          : ROUTES.VIEW_AUDIT_SHEET.replace(':uploadId', chart.id),
      };
    });

    return [batchItem, ...chartItems];
  }, [batch, currentUser]);

  const activeIndex = useMemo(() => {
    if (!items.length) return -1;
    if (isBatchView) return 0;
    return items.findIndex(item => item.key === String(currentChartId));
  }, [items, isBatchView, currentChartId]);

  if (!batchId || !items.length) return null;

  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;
  const previousItem = activeIndex > 0 ? items[activeIndex - 1] : null;
  const nextItem =
    activeIndex >= 0 && activeIndex < items.length - 1
      ? items[activeIndex + 1]
      : null;

  // Locked while the sheet is saving, or while a navigation is already under
  // way, so a click cannot leave the sheet before its changes land.
  const isLocked = isSaving || isNavigating;

  function showStillSaving() {
    toast.showWarning({
      description:
        'Your latest changes are still being saved. Please wait a moment, then move to another chart.',
    });
  }

  async function goTo(item) {
    if (!item || isNavigating) return;
    // The controls are disabled while saving, but an open menu can still take
    // a click in that window.
    if (isSaving) {
      showStillSaving();
      return;
    }
    try {
      setIsNavigating(true);
      // Leaving this sheet discards whatever is on screen, so only go once the
      // container confirms the pending work is saved. A save still running or
      // a failed save keeps us here, each with its own message.
      if (onNavigateAway) {
        const result = await onNavigateAway();
        if (result === NAVIGATE_AWAY.SAVING) {
          showStillSaving();
          return;
        }
        if (result !== NAVIGATE_AWAY.OK) {
          toast.showError({
            description:
              'Could not save your changes to this sheet, so it was not closed. Please retry before moving to another chart.',
          });
          return;
        }
      }
      navigate(item.route);
    } finally {
      setIsNavigating(false);
    }
  }

  function describe(item) {
    if (!item) return '';
    if (item.isBatch) return `${item.title} (${item.caption})`;
    return `${item.title} · ${item.isEditable ? 'Editable' : 'View only'} · ${
      item.auditor
    }`;
  }

  const renderArrow = (item, direction) => {
    const Icon = direction === 'previous' ? ChevronLeftIcon : ChevronRightIcon;
    const isDisabled = !item || isLocked;

    let label = `${direction === 'previous' ? 'Previous' : 'Next'}: ${describe(item)}`;
    if (!item) label = `No ${direction} sheet in this batch`;
    else if (isSaving) label = 'Saving your changes...';

    return (
      <Tooltip hasArrow placement="top" label={label}>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
          boxSize={'26px'}
          borderRadius={'full'}
          bg={isDisabled ? 'whiteAlpha.400' : 'white'}
          opacity={isDisabled ? 0.5 : 1}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          _hover={isDisabled ? {} : { bg: AppColors.gray }}
          onClick={isDisabled ? undefined : () => goTo(item)}
          aria-disabled={isDisabled}
          aria-label={`${direction === 'previous' ? 'Previous' : 'Next'} sheet in batch`}
        >
          <Icon color={AppColors.secondary} boxSize={'16px'} />
        </Flex>
      </Tooltip>
    );
  };

  const renderMenuItem = item => {
    const isActive = item.key === activeItem?.key;

    return (
      <MenuItem
        key={item.key}
        onClick={() => goTo(item)}
        bg={isActive ? AppColors.lightBlue : 'white'}
        _hover={{ bg: AppColors.gray }}
        px={3}
        py={2}
      >
        <Flex width={'100%'} alignItems={'center'} gap={3}>
          <Box flex={1} minW={0}>
            <Text
              fontFamily={FONT_FAMILY.ptSans}
              fontSize={'sm'}
              fontWeight={isActive ? 'bold' : 'semibold'}
              color={AppColors.secondary}
              noOfLines={1}
            >
              {item.title}
            </Text>
            <Text
              fontFamily={FONT_FAMILY.ptSans}
              fontSize={'xs'}
              color={AppColors.darkGray}
              noOfLines={1}
            >
              {item.isBatch
                ? `All charts · ${item.caption}`
                : `${item.caption} · ${item.status} · ${item.auditor}`}
            </Text>
          </Box>
          {!item.isBatch && (
            <Text
              fontFamily={FONT_FAMILY.ptSans}
              fontSize={'10px'}
              fontWeight={'bold'}
              whiteSpace={'nowrap'}
              px={2}
              py={'2px'}
              borderRadius={'full'}
              color={item.isEditable ? AppColors.green : AppColors.darkGray}
              bg={item.isEditable ? '#E7F5E7' : AppColors.gray}
            >
              {item.isEditable ? 'Editable' : 'View only'}
            </Text>
          )}
        </Flex>
      </MenuItem>
    );
  };

  return (
    <HStack spacing={2} alignItems={'center'}>
      {renderArrow(previousItem, 'previous')}

      <Menu placement="top-start" autoSelect={false}>
        <MenuButton
          type="button"
          aria-label="Charts in this batch"
          bg={'white'}
          borderRadius={'full'}
          px={3}
          py={1}
          maxW={'320px'}
          disabled={isLocked}
          _hover={{ bg: AppColors.gray }}
          _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
        >
          <HStack spacing={2}>
            <Text
              fontFamily={FONT_FAMILY.ptSans}
              fontSize={'sm'}
              fontWeight={'bold'}
              color={AppColors.secondary}
              noOfLines={1}
            >
              {activeItem ? activeItem.title : 'Charts in this batch'}
            </Text>
            {(isSaving || activeItem) && (
              <Text
                fontFamily={FONT_FAMILY.ptSans}
                fontSize={'xs'}
                color={AppColors.darkGray}
                whiteSpace={'nowrap'}
              >
                {isSaving ? 'Saving...' : activeItem.caption}
              </Text>
            )}
            <ChevronUpIcon color={AppColors.secondary} />
          </HStack>
        </MenuButton>

        <MenuList maxH={'320px'} overflowY={'auto'} minW={'320px'} zIndex={20}>
          <Text
            px={3}
            pb={1}
            fontFamily={FONT_FAMILY.ptSans}
            fontSize={'xs'}
            fontWeight={'bold'}
            color={AppColors.darkGray}
          >
            Batch {batch.batchName || batch.batchId}
          </Text>
          {renderMenuItem(items[0])}
          <MenuDivider />
          {items.slice(1).map(renderMenuItem)}
        </MenuList>
      </Menu>

      {renderArrow(nextItem, 'next')}
    </HStack>
  );
}
